<template>
  <div class="city-army">
    <h2 class="panel-title">{{ uiStore.isEditingDefense ? "Defense Formation" : "Garrison" }}</h2>
    <section v-if="uiStore.isEditingDefense" class="hero-deploy">
      <h3 class="place-label">Hero</h3>
      <p v-if="!gameStore.playerHeroes.length" class="place-hint">
        Activate a hero to assign them to the wall.
      </p>
      <ul v-else class="hero-list">
        <li
          v-for="hero in gameStore.playerHeroes"
          :key="hero.id"
          :class="{
            selectable: canSelectHero(hero),
            active: gameStore.defenseSelectedHeroId === hero.id,
            busy: !canSelectHero(hero),
          }"
          @click="onSelectHero(hero)"
        >
          <HeroTagChip
            block
            :name="heroDisplayName(hero)"
            :meta="`Hero · Level ${hero.level || 1}`"
            :image="socketedCard(hero)?.image || ''"
            :image-position="socketedCard(hero)?.image_position"
            :rarity="socketedCard(hero)?.rarity || 'common'"
          >
            <span v-if="isHeroBusy(hero)" class="unit-status">On a mission</span>
            <span v-else-if="!socketedCard(hero)" class="unit-status">
              Socket a hero card
            </span>
          </HeroTagChip>
        </li>
      </ul>
      <p v-if="gameStore.defenseSelectedHeroId" class="place-hint">
        Click a defense tile to deploy the hero.
      </p>
    </section>
    <ul class="economy-list">
      <li
        v-for="row in gameStore.activeCityArmy"
        :key="row.slug"
        :class="{
          locked: row.locked,
          selectable: canSelect(row),
          active: uiStore.isEditingDefense && gameStore.defenseSelectedSlug === row.slug,
        }"
        :title="row.locked ? row.lockReasons.join(' · ') : undefined"
        @click="onSelectUnit(row)"
      >
        <i :class="row.icon" :style="{ color: row.color }"></i>
        <div class="unit-copy">
          <span class="economy-name">
            {{ row.name }}
            <i v-if="row.locked" class="fas fa-lock lock-icon" aria-hidden="true"></i>
          </span>
          <span v-if="row.locked" class="unit-lock">{{ row.lockReasons.join(" · ") }}</span>
          <span v-else-if="row.description" class="unit-desc">{{ row.description }}</span>
          <span v-if="row.hp != null" class="unit-stats">
            HP {{ row.hp }} · ATK {{ row.atk }} · DEF {{ row.def }}
          </span>
        </div>
        <span
          class="economy-amount reserve"
          :title="uiStore.isEditingDefense ? 'Unassigned reserve' : 'Empire Total'"
          :aria-label="stockLabel(row)"
        >{{ stockAmount(row) }}</span>
        <span
          v-if="!uiStore.isEditingDefense"
          class="economy-amount"
          :class="row.yield > 0 && !row.locked ? 'yield' : 'yield-idle'"
        >+{{ row.yield }}/h</span>
      </li>
    </ul>

    <div v-if="uiStore.isEditingDefense && selectedRow" class="place-controls">
      <label class="place-label" for="defense-qty">
        Place {{ selectedRow.name }}
      </label>
      <div class="slider-row">
        <input
          id="defense-qty-range"
          type="range"
          min="0"
          :max="sliderMax"
          :value="gameStore.defensePlaceQty"
          :disabled="sliderMax < 1"
          @input="gameStore.setDefensePlaceQty($event.target.value)"
        />
        <input
          id="defense-qty"
          class="qty-input"
          type="number"
          min="0"
          :max="sliderMax"
          :value="gameStore.defensePlaceQty"
          :disabled="sliderMax < 1"
          @input="gameStore.setDefensePlaceQty($event.target.value)"
        />
      </div>
      <p class="place-hint">
        {{ sliderMax < 1 ? "No unassigned troops of this type." : `Click a grid cell to deploy ${gameStore.defensePlaceQty}.` }}
      </p>
    </div>

    <p v-if="gameStore.defenseSaveError" class="save-error">{{ gameStore.defenseSaveError }}</p>

    <div class="formation-actions">
      <button
        v-if="!uiStore.isEditingDefense"
        type="button"
        class="formation-btn"
        :disabled="!gameStore.activeCity"
        @click="gameStore.startDefenseEdit"
      >
        Edit Formation
      </button>
      <template v-else>
        <button
          type="button"
          class="formation-btn ghost"
          :disabled="gameStore.isSavingDefense"
          @click="gameStore.cancelDefenseEdit"
        >
          Cancel
        </button>
        <button
          type="button"
          class="formation-btn"
          :disabled="gameStore.isSavingDefense"
          @click="onSave"
        >
          {{ gameStore.isSavingDefense ? "Saving…" : "Save Formation" }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from "vue";
import { useGameStore } from "../store/game";
import { useUiStore } from "../store/ui";
import HeroTagChip from "./HeroTagChip.vue";

const gameStore = useGameStore();
const uiStore = useUiStore();

const sliderMax = computed(() => Number(gameStore.defenseSliderMax) || 0);

const selectedRow = computed(() =>
  (gameStore.activeCityArmy || []).find(
    (row) => row.slug === gameStore.defenseSelectedSlug
  ) || null
);

const remainingFor = (slug) =>
  Math.max(0, Math.floor(Number(gameStore.defenseRemaining?.[slug]) || 0));

watch(
  () => gameStore.defenseSliderMax,
  () => gameStore.clampDefensePlaceQty()
);

const stockAmount = (row) =>
  uiStore.isEditingDefense ? remainingFor(row.slug) : row.reserve;

const stockLabel = (row) =>
  uiStore.isEditingDefense
    ? `${stockAmount(row)} ${row.name} unassigned`
    : `${row.reserve} ${row.name}, Empire Total`;

const canSelect = (row) =>
  uiStore.isEditingDefense && !row.locked && remainingFor(row.slug) > 0;

const onSelectUnit = (row) => {
  if (!uiStore.isEditingDefense || row.locked) return;
  if (remainingFor(row.slug) < 1 && gameStore.defenseSelectedSlug !== row.slug) {
    return;
  }
  gameStore.setDefenseSelectedSlug(
    gameStore.defenseSelectedSlug === row.slug ? null : row.slug
  );
};

const heroDisplayName = (hero) => {
  if (!hero) return "Hero";
  return hero.name || `Hero #${hero.id}`;
};

const socketedCard = (hero) => gameStore.socketedHeroCard(hero?.id);

const isHeroBusy = (hero) => gameStore.inProgressHeroIds.has(hero?.id);

const canSelectHero = (hero) =>
  uiStore.isEditingDefense && !!hero && !isHeroBusy(hero) && !!socketedCard(hero);

const onSelectHero = (hero) => {
  if (!canSelectHero(hero)) return;
  gameStore.setDefenseSelectedHeroId(hero.id);
};

const onSave = async () => {
  try {
    await gameStore.saveCityDefense();
  } catch {
    /* error shown via defenseSaveError */
  }
};
</script>

<style scoped>
.city-army {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.panel-title {
  font-family: "Orbitron", sans-serif;
  color: #00f6ff;
  font-size: 1.5rem;
  margin-top: 0;
  margin-bottom: 0;
  text-align: center;
}

.hero-deploy {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.hero-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hero-list li {
  border-radius: 8px;
  border: 1px solid transparent;
}

.hero-list li.selectable {
  cursor: pointer;
}

.hero-list li.selectable:hover {
  border-color: rgba(0, 246, 255, 0.35);
}

.hero-list li.active {
  border-color: #f0d060;
  box-shadow: 0 0 10px rgba(240, 208, 96, 0.35);
}

.hero-list li.busy {
  cursor: not-allowed;
  opacity: 0.55;
}

.unit-status {
  font-size: 0.75rem;
  color: #ebcb8b;
}

.economy-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.economy-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.economy-list li.locked {
  opacity: 0.5;
  filter: grayscale(1);
}

.economy-list li.selectable {
  cursor: pointer;
}

.economy-list li.selectable:hover {
  border-color: rgba(0, 246, 255, 0.35);
}

.economy-list li.active {
  border-color: #00f6ff;
  box-shadow: 0 0 10px rgba(0, 246, 255, 0.25);
  background: rgba(0, 40, 50, 0.55);
}

.economy-list i:first-child {
  width: 22px;
  text-align: center;
  font-size: 1.05rem;
  flex-shrink: 0;
}

.unit-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.economy-name {
  color: #d8d0c0;
  font-size: 0.9rem;
}

.lock-icon {
  margin-left: 6px;
  font-size: 0.7rem;
  color: #8892b0;
}

.unit-desc {
  color: #8892b0;
  font-size: 0.75rem;
  line-height: 1.35;
}

.unit-lock {
  color: #c4b5a0;
  font-size: 0.75rem;
  line-height: 1.35;
}

.unit-stats {
  color: #a9b1d6;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
}

.economy-amount {
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.economy-amount.reserve {
  color: #fff;
  cursor: help;
  min-width: 2ch;
  text-align: right;
}

.economy-amount.yield {
  color: #7dffa3;
}

.economy-amount.yield-idle {
  color: #7a8490;
}

.place-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 10px;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(0, 246, 255, 0.18);
  border-radius: 8px;
}

.place-label {
  font-family: "Cinzel", serif;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9b48a;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

input[type="range"] {
  flex: 1;
  cursor: pointer;
  accent-color: #00f0ff;
}

.qty-input {
  width: 4.25rem;
  padding: 0.3rem 0.4rem;
  border-radius: 4px;
  border: 1px solid rgba(0, 246, 255, 0.35);
  background: rgba(8, 12, 24, 0.85);
  color: #00f0ff;
  font-family: monospace;
  font-size: 0.95rem;
  text-align: right;
}

.place-hint {
  margin: 0;
  color: #8892b0;
  font-size: 0.75rem;
  line-height: 1.35;
}

.save-error {
  margin: 0;
  color: #bf616a;
  font-size: 0.8rem;
}

.formation-actions {
  display: flex;
  gap: 8px;
}

.formation-btn {
  flex: 1;
  padding: 0.75rem 0.9rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(45deg, #0077ff, #00f0ff);
  color: #061018;
  font-family: "Orbitron", sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
}

.formation-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.formation-btn.ghost {
  background: rgba(255, 255, 255, 0.08);
  color: #d8d0c0;
  border: 1px solid rgba(255, 255, 255, 0.12);
}
</style>
